import { useEffect, useRef, useState } from "react";

const DURATION_MS = 650;

const easeOut = (t: number): number => 1 - (1 - t) ** 3;

/**
 * Tweens towards `target` whenever it changes, so a headline number counts
 * up on first paint and slides on every change instead of jumping.
 */
export const useAnimatedNumber = (target: number): number => {
  const [value, setValue] = useState(0);
  const from = useRef(0);
  const frame = useRef<number | null>(null);

  useEffect(() => {
    const start = performance.now();
    const origin = from.current;
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / DURATION_MS);
      const next = origin + (target - origin) * easeOut(t);
      setValue(next);
      if (t < 1) {
        frame.current = requestAnimationFrame(step);
      } else {
        from.current = target;
      }
    };
    if (frame.current !== null) {
      cancelAnimationFrame(frame.current);
    }
    frame.current = requestAnimationFrame(step);
    return () => {
      if (frame.current !== null) {
        cancelAnimationFrame(frame.current);
      }
      from.current = target;
    };
  }, [target]);

  return value;
};
